import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Button } from "../../../../components/ui/button";
import { Card, CardContent } from "../../../../components/ui/card";
import { Input } from "../../../../components/ui/input";
import Image from "next/image";
import Link from "next/link";
import { FaInstagram, FaTwitter, FaFacebook, FaLinkedin } from "react-icons/fa";

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const FooterSection = () => {
  const logoRef = useRef(null);
  const columnsRef = useRef(null);
  const cardRef = useRef(null);
  const copyrightRef = useRef(null);

  const logoInView = useInView(logoRef, { once: true, margin: "-100px" });
  const columnsInView = useInView(columnsRef, { once: true, margin: "-100px" });
  const cardInView = useInView(cardRef, { once: true, margin: "-100px" });
  const copyrightInView = useInView(copyrightRef, { once: true });

  const contactColumns = [
    {
      title: "Contact Us",
      links: [
        { name: "Customer Support", href: "/support" },
        { name: "Trip Application Help", href: "/trip-help" },
        { name: "Cancellation Policy", href: "/CancellationPolicy" },
        { name: "Travel FAQs", href: "/faqs" },
        { name: "Request a Callback", href: "/callback" },
      ],
    },
    {
      title: "About Outcome Outing",
      links: [
        { name: "Our Story", href: "/AboutUs/our-story" },
        { name: "Meet the Founders", href: "/Mentors" },
        { name: "Vision & Values", href: "/Vision" },
        { name: "Terms & Conditions", href: "/AboutUs/terms-condition" },
        { name: "Privacy Policy", href: "/privacy-policy" },
        { name: "Work with Us", href: "https://jobs.outcomeouting.com/" },
      ],
    },
    {
      title: "Community & Collaborations",
      links: [
        { name: "Become a Mentor", href: "/mentor" },
        { name: "Join as a Local Host", href: "/host" },
        { name: "Partner With Us", href: "/partner" },
        { name: "Refer & Earn", href: "/refer" },
        { name: "Alumni Network Access", href: "/alumni" },
      ],
    },
    {
      title: "Need Help?",
      content:
        "We’re here 24/7 — message us or call anytime. Real people, real responses.",
    },
  ];

  const socialLinks = [
    {
      name: "Instagram",
      href: "https://www.instagram.com/outcome_outing/",
      icon: <FaInstagram size={24} />,
    },
    {
      name: "Twitter",
      href: "https://x.com/OutcomeOuting",
      icon: <FaTwitter size={24} />,
    },
    {
      name: "Facebook",
      href: "https://www.facebook.com/people/Outcome-Outing/61576746976802/",
      icon: <FaFacebook size={24} />,
    },
    {
      name: "LinkedIn",
      href: "https://www.linkedin.com/company/the-outcome-outing/",
      icon: <FaLinkedin size={24} />,
    },
  ];

  return (
    <footer className="w-full bg-[#FFFFFFE6] border-t border-[#000000] py-8 sm:py-12 lg:py-16">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Logo, tagline, and social icons */}
        <motion.div
          ref={logoRef}
          variants={fadeInUp}
          initial="hidden"
          animate={logoInView ? "visible" : "hidden"}
          className="flex flex-col lg:flex-row justify-between mb-8 sm:mb-12 lg:mb-16 gap-8"
        >
          <div className="flex flex-col gap-4 sm:gap-6 max-w-[280px]">
            <Link href="/">
              <Image
                width={180}
                height={74}
                src="/logoo.svg"
                alt="Travatas Logo"
                className="object-contain hover:scale-105 transition-transform duration-300"
              />
            </Link>
            <p className="text-[#0D0D0D] text-sm sm:text-base leading-snug font-jakarta">
              Book with intention. Travel with transformation.
              <br />
              Your journey begins before the trip — and lasts long after.
            </p>
            <div className="flex gap-4 mt-4">
              {socialLinks.map((social, index) => (
                <Link
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#0D0D0D] hover:text-[#ffae00] transition-colors"
                  aria-label={social.name}
                >
                  {social.icon}
                </Link>
              ))}
            </div>
          </div>

          <motion.div
            ref={columnsRef}
            className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 lg:gap-12"
            variants={fadeInUp}
            initial="hidden"
            animate={columnsInView ? "visible" : "hidden"}
          >
            {contactColumns.map((column, index) => (
              <div key={index} className="flex flex-col gap-3">
                <h3 className="font-bold text-[#0D0D0D] text-lg sm:text-xl font-bricolage">
                  {column.title}
                </h3>
                {column.links ? (
                  <div className="text-[#0D0D0D] text-sm sm:text-base font-jakarta space-y-2">
                    {column.links.map((link, i) => (
                      <Link
                        key={i}
                        href={link.href}
                        className="block hover:underline hover:text-[#ffae00] transition-colors"
                      >
                        {link.name}
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-[#0D0D0D] text-sm sm:text-base font-jakarta">
                    {column.content}
                  </p>
                )}
              </div>
            ))}
          </motion.div>
        </motion.div>

        {/* Contact Card */}
        <motion.div
          ref={cardRef}
          className="mb-8 sm:mb-12"
          variants={fadeInUp}
          initial="hidden"
          animate={cardInView ? "visible" : "hidden"}
        >
          <Card className="bg-[#ffae00] rounded-2xl border-2 border-white shadow-lg">
            <CardContent className="p-4 sm:p-6 lg:p-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Help */}
                <div className="flex flex-col gap-3 justify-center items-center text-center">
                  <p className="font-semibold text-[#0D0D0D] text-base sm:text-lg font-bricolage">
                    Need to Talk?
                  </p>
                  <Link
                    href="tel:+917982579691"
                    className="font-bold text-[#0D0D0D] text-lg sm:text-xl underline hover:text-white font-bricolage"
                  >
                    +91 7982579691
                  </Link>
                  <Link
                    href="tel:+919999052908"
                    className="font-bold text-[#0D0D0D] text-lg sm:text-xl underline hover:text-white font-bricolage"
                  >
                    +91 9999052908
                  </Link>
                  <p className="font-bold text-[#0D0D0D] text-lg sm:text-xl font-bricolage">
                    We’re just a ring away.
                  </p>
                </div>

                {/* Message */}
                <Card className="bg-[#ffae00] rounded-2xl border-2 border-[#0D0D0D]">
                  <CardContent className="flex flex-col items-center p-4 sm:p-6">
                    <h3 className="font-bold text-[#0D0D0D] text-lg sm:text-2xl text-center mb-3 font-bricolage">
                      Send us a message
                    </h3>
                    <p className="text-[#0D0D0D] text-sm sm:text-base text-center font-jakarta mb-4">
                      Have questions or want a callback? Drop us a message and
                      we’ll get back shortly.
                    </p>
                    <Link
                      href="https://wa.me/917982579691"
                      target="_blank"
                      className="bg-green-500 text-white px-4 py-2 rounded-full hover:bg-green-600 transition-colors font-jakarta"
                    >
                      Message on WhatsApp
                    </Link>
                  </CardContent>
                </Card>

                {/* Newsletter */}
                <div className="flex flex-col items-center gap-4">
                  <h3 className="font-bold text-[#0D0D0D] text-lg sm:text-2xl text-center font-bricolage">
                    Join the Inner Circle
                  </h3>
                  <p className="text-[#0D0D0D] text-sm sm:text-base text-center font-jakarta max-w-[300px]">
                    Get first access to upcoming trips, offers, and
                    behind-the-scenes stories.
                  </p>
                  <div className="w-full max-w-[280px] sm:max-w-[300px]">
                    <Input
                      className="w-full bg-white rounded-2xl shadow-md h-10 sm:h-11"
                      placeholder="Enter your email"
                    />
                    <motion.div whileHover={{ scale: 1.05 }} className="mt-3">
                      <Button className="w-full bg-[#063258] text-white hover:bg-[#0D0D0D] hover:text-white rounded-2xl shadow-md h-10 sm:h-11 font-bold  text-lg font-bricolage">
                        Subscribe
                      </Button>
                    </motion.div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Copyright */}
        <motion.div
          ref={copyrightRef}
          className="text-center"
          variants={fadeInUp}
          initial="hidden"
          animate={copyrightInView ? "visible" : "hidden"}
        >
          <p className="text-[#0D0D0D] text-xs sm:text-sm font-bricolage">
            © 2025 Outcome Outing
          </p>
        </motion.div>
      </div>
    </footer>
  );
};

export default FooterSection;